// src/components/packages/FormAddEditPackage.jsx

import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Stack, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreatePackage from "../../hooks/packages/useCreatePackage";
import useUpdatePackage from "../../hooks/packages/useUpdatePackage";
import FormInput from "../inputs/FormInput";
import i18next from "i18next";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditPackage = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [price, setPrice] = useState("");
    const [status, setStatus] = useState("active");

    const { createPackageMutate, isPending: isCreating } = useCreatePackage();
    const { editPackage, isUpdate } = useUpdatePackage();

    const loadingSubmit = isCreating || isUpdate;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setDescriptionAr(currentData?.description?.ar || "");
            setDescriptionEn(currentData?.description?.en || "");
            setPrice(currentData?.price || "");
            setStatus(currentData?.status || "active");
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();

        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            description: {
                ar: descriptionAr || descriptionEn || "",
                en: descriptionEn || descriptionAr || "",
            },
            price: parseFloat(price),
            status,
        };

        if (isEdit && currentData?.id) {
            editPackage(
                { id: currentData.id, req: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                    },
                }
            );
        } else {
            createPackageMutate(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                },
            });
        }
    };

    const statusOptions = [
        { id: "active", name: isArabic ? "نشط" : "Active" },
        { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
    ];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={SubmitForm}
                sx={{
                    p: { xs: 2, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {isEdit
                            ? isArabic
                                ? "تعديل خيارات الجوله"
                                : "Edit Tour Options"
                            : isArabic
                                ? "إضافة خيارات الجوله جديدة"
                                : "Create New Tour Options"}
                    </Typography>

                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                </Box>

                <Box mb={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "السعر" : "Price"}
                                placeholder={isArabic ? "أدخل سعر النشاط" : "Enter Tour Options price"}
                                type="number"
                                name="price"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                required
                                inputProps={{ step: "0.01", min: "0" }}
                            />
                        </Grid>
                        {showArabic && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="الاسم (عربي)"
                                        placeholder="أدخل اسم النشاط بالعربي"
                                        type="text"
                                        name="name_ar"
                                        value={nameAr}
                                        onChange={(e) => setNameAr(e.target.value)}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormInput
                                        label="الوصف (عربي)"
                                        placeholder="أدخل وصف النشاط بالعربي"
                                        type="editor"  // ✅ الجديد
                                        name="description_ar"
                                        value={descriptionAr}
                                        onChange={(e) => setDescriptionAr(e.target.value)}
                                        textareaRows="250px"
                                    />
                                </Grid>
                            </>
                        )}

                        {showEnglish && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Name (English)"
                                        placeholder="Enter Tour Options name in English"
                                        type="text"
                                        name="name_en"
                                        value={nameEn}
                                        onChange={(e) => setNameEn(e.target.value)}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormInput
                                        label="Description (English)"
                                        placeholder="Enter Tour Options description in English"
                                        type="editor"
                                        name="description_en"
                                        value={descriptionEn}
                                        onChange={(e) => setDescriptionEn(e.target.value)}
                                        textareaRows="250px"
                                    />
                                </Grid>
                            </>
                        )}



                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الحالة" : "Status"}
                                type="select"
                                name="status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                options={statusOptions}
                                required
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loadingSubmit}
                        >
                            {loadingSubmit
                                ? isArabic
                                    ? "جارِ الحفظ..."
                                    : "Saving..."
                                : isEdit
                                    ? isArabic
                                        ? "تحديث"
                                        : "Update"
                                    : isArabic
                                        ? "حفظ"
                                        : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditPackage;
